/**
 * Utility functions for working with frameworks and ISO clauses
 */

// Document ID to framework name mapping - matches FrameworkCombobox options
export const documentToFramework: Record<string, string> = {
  // ISO Standards
  'ISO27001': 'ISO 27001',
  'ISO27001-2013': 'ISO 27001',
  'ISO27001:2013': 'ISO 27001',
  'ISO27001:2022': 'ISO 27001',
  'ISO270012022ANNEXACONTROLS': 'ISO 27001',
  'ISO270012022CONTROLS': 'ISO 27001',
  'ISO270012013ANNEXACONTROLS': 'ISO 27001',
  'ISO270012013CONTROLS': 'ISO 27001',
  
  // Other frameworks
  'SOC2': 'SOC 2',
  'SOC2CONTROLS': 'SOC 2',
  'NIST80053': 'NIST 800-53',
  'NIST80053REV5': 'NIST 800-53',
  'NIST-800-53-REV5': 'NIST 800-53',
  'ISM': 'ISM',
  'PACONTROLS': 'Privacy Act',
  'PRIVACYACT': 'Privacy Act',
  'DISP': 'DISP',
  'DISPCONTROLS': 'DISP',
  'SMB1001': 'SMB 1001',
  'SMB1001CONTROLS': 'SMB 1001',
  'QGOVIS18': 'QGOV-IS18',
  'QGOV-IS18': 'QGOV-IS18',
  
  // Other ISO standards
  'ISO9001': 'ISO 9001',
  'ISO90012015': 'ISO 9001',
  'ISO90012015ANNEXACONTROLS': 'ISO 9001',
  'ISO90012015CONTROLS': 'ISO 9001',
  
  'ISO45001': 'ISO 45001',
  'ISO450012018': 'ISO 45001',
  'ISO450012018ANNEXACONTROLS': 'ISO 45001',
  'ISO450012018CONTROLS': 'ISO 45001',
  
  'ISO14001': 'ISO 14001',
  'ISO140012015': 'ISO 14001',
  'ISO140012015ANNEXACONTROLS': 'ISO 14001',
  'ISO140012015CONTROLS': 'ISO 14001',
  
  'ISO27701': 'ISO 27701',
  'ISO277012019': 'ISO 27701',
  'ISO277012019ANNEXACONTROLS': 'ISO 27701',
  'ISO277012019CONTROLS': 'ISO 27701',
  
  'ISO42001': 'ISO 42001',
  'ISO42001-2022': 'ISO 42001',
  'ISO42001-2022ANNEXACONTROLS': 'ISO 42001',
  'ISO42001-2022CONTROLS': 'ISO 42001',
};

/**
 * Extract a normalized document ID from an ISO clause string
 * @param clause ISO clause string (e.g., "ISO270012022ANNEXACONTROLS:A.5.1")
 * @returns Normalized document ID for framework mapping
 */
export const getNormalizedDocId = (clause: string): string => {
  if (!clause) return '';
  
  // Extract document ID (everything before the first colon)
  const docId = clause.split(':')[0] || '';
  // Normalize by removing non-alphanumeric chars and converting to uppercase
  return docId.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
};

/**
 * Extract control ID from an ISO clause string
 * @param clause ISO clause string (e.g., "ISO270012022ANNEXACONTROLS:A.5.1")
 * @returns Control ID (everything after the first colon)
 */
export const getControlId = (clause: string): string => {
  if (!clause || !clause.includes(':')) return '';
  
  // Extract everything after the first colon
  const parts = clause.split(':');
  return parts.slice(1).join(':'); // Join back in case there are multiple colons
};

/**
 * Get the framework display name from a document ID using the mapping table
 * @param docId Normalized document ID
 * @returns Mapped framework name or "Custom" if not found
 */
export const getFrameworkNameFromDocId = (docId: string): string => {
  // Direct lookup in the mapping table
  const exactMatch = documentToFramework[docId];
  if (exactMatch) return exactMatch;
  
  // Fallback to partial matching
  for (const [key, value] of Object.entries(documentToFramework)) {
    if (docId.includes(key)) {
      return value;
    }
  }
  
  // If all else fails, try the standard pattern matching
  if (docId.includes('ISO27001')) return 'ISO 27001';
  if (docId.includes('SOC2')) return 'SOC 2';
  if (docId.includes('NIST80053') || docId.includes('NIST800-53')) return 'NIST 800-53';
  if (docId.includes('ISM')) return 'ISM';
  if (docId.includes('PACONTROLS') || docId.includes('PRIVACYACT')) return 'Privacy Act';
  if (docId.includes('DISP')) return 'DISP';
  if (docId.includes('SMB1001')) return 'SMB 1001';
  if (docId.includes('QGOV') || docId.includes('IS18')) return 'QGOV-IS18';
  if (docId.includes('ISO9001')) return 'ISO 9001';
  if (docId.includes('ISO45001')) return 'ISO 45001';
  if (docId.includes('ISO14001')) return 'ISO 14001';
  if (docId.includes('ISO27701')) return 'ISO 27701';
  if (docId.includes('ISO42001')) return 'ISO 42001';
  
  return 'Custom';
};

/**
 * Get framework display name from an ISO clause string
 * @param clause ISO clause string (e.g., "ISO270012022ANNEXACONTROLS:A.5.1")
 * @returns Framework name (e.g., "ISO 27001")
 */
export const getFrameworkNameFromClause = (clause?: string): string => {
  if (!clause) return 'Custom';
  
  const docId = getNormalizedDocId(clause);
  return getFrameworkNameFromDocId(docId);
};

/**
 * Get framework display name from an array of ISO clause strings
 * @param isoClauses Array of ISO clause strings
 * @returns Framework name or "Custom" if not found
 */
export const getFrameworkFromIsoClauses = (isoClauses?: string[]): string => {
  if (!isoClauses || isoClauses.length === 0) return 'Custom';
  
  // Try each clause until we find a match
  for (const clause of isoClauses) {
    if (!clause) continue;
    
    const frameworkName = getFrameworkNameFromClause(clause);
    if (frameworkName !== 'Custom') {
      return frameworkName;
    }
  }
  
  return 'Custom';
};

/**
 * Extract control IDs from array of ISO clauses
 * @param isoClauses Array of ISO clause strings
 * @returns Array of control IDs
 */
export const extractControlIds = (isoClauses?: string[]): string[] => {
  if (!isoClauses) return [];
  
  return isoClauses
    .filter(clause => clause && clause.includes(':'))
    .map(clause => getControlId(clause))
    .filter(Boolean);
};

/**
 * Get framework info (name and control IDs) from ISO clauses
 * @param isoClauses Array of ISO clause strings
 * @returns Object with framework name and array of control IDs
 */
export const getFrameworkInfo = (isoClauses?: string[]) => {
  if (!isoClauses || isoClauses.length === 0) {
    return { name: 'Custom', controls: [] };
  }
  
  return {
    name: getFrameworkFromIsoClauses(isoClauses),
    controls: extractControlIds(isoClauses)
  };
};

/**
 * Format ISO clauses into compact pill format (e.g., "(ISO: ISO27001:A.5.13)")
 * @param isoClauses Array of ISO clause strings
 * @returns Array of formatted pill strings
 */
export const formatIsoClausesAsPills = (isoClauses?: string[]): string[] => {
  if (!isoClauses || isoClauses.length === 0) return [];
  
  const pills: string[] = [];
  
  isoClauses.forEach(clause => {
    if (!clause || !clause.includes(':')) return;
    
    const frameworkName = getFrameworkNameFromClause(clause);
    const controlId = getControlId(clause);
    
    if (frameworkName && controlId) {
      pills.push(`(${frameworkName}: ${controlId})`);
    }
  });
  
  return pills;
};
